import React, { useEffect } from 'react';
import {
  Box, Typography, Stack, Avatar, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  Assignment, Hotel, DirectionsCar, Payment, Phone, Warning,
  CheckCircle, Note, FlightTakeoff, Cancel, Feedback,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { loadTripActivities } from '../../../reduxcomponents/slices/tripManagementSlice';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const ACTIVITY_ICONS = {
  'Trip Assigned':       { icon: Assignment,    color: 'primary' },
  'Trip Reassigned':     { icon: Assignment,    color: 'warning' },
  'Trip Status Changed': { icon: FlightTakeoff, color: 'info' },
  'Hotel Confirmed':     { icon: Hotel,         color: 'success' },
  'Hotel Updated':       { icon: Hotel,         color: 'info' },
  'Driver Confirmed':    { icon: DirectionsCar, color: 'success' },
  'Driver Updated':      { icon: DirectionsCar, color: 'info' },
  'Payment Collected':   { icon: Payment,       color: 'success' },
  'Customer Called':     { icon: Phone,         color: 'info' },
  'Note Added':          { icon: Note,          color: 'default' },
  'Complaint Raised':    { icon: Warning,       color: 'error' },
  'Complaint Resolved':  { icon: CheckCircle,   color: 'success' },
  'Escalation Raised':   { icon: Warning,       color: 'error' },
  'Escalation Resolved': { icon: CheckCircle,   color: 'success' },
  'Feedback Collected':  { icon: Feedback,      color: 'secondary' },
  'Trip Completed':      { icon: CheckCircle,   color: 'success' },
  'Trip Cancelled':      { icon: Cancel,        color: 'error' },
  'Checklist Updated':   { icon: CheckCircle,   color: 'info' },
};

const TimelineItem = ({ activity, isLast }) => {
  const cfg = ACTIVITY_ICONS[activity.activity_type] || { icon: Note, color: 'default' };
  const Icon = cfg.icon;

  return (
    <Stack direction="row" spacing={2}>
      {/* Connector line */}
      <Box display="flex" flexDirection="column" alignItems="center">
        <Avatar sx={{ bgcolor: `${cfg.color}.100`, width: 36, height: 36 }}>
          <Icon sx={{ color: `${cfg.color}.main`, fontSize: 18 }} />
        </Avatar>
        {!isLast && <Box flex={1} width="2px" bgcolor="divider" mt={0.5} />}
      </Box>

      {/* Content */}
      <Box pb={isLast ? 0 : 2} flex={1}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
          <Box>
            <Typography variant="body2" fontWeight={600}>{activity.activity_type}</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>{activity.description}</Typography>
          </Box>
          <Typography variant="caption" color="text.disabled" flexShrink={0}>
            {dayjs(activity.created_at).fromNow()}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} mt={0.75} alignItems="center">
          <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
            {activity.performed_by?.fullName?.[0] || '?'}
          </Avatar>
          <Typography variant="caption" color="text.secondary">
            {activity.performed_by?.fullName || 'Unknown'}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            • {dayjs(activity.created_at).format('DD MMM YYYY, h:mm A')}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
};

const ActivityTimeline = ({ tripId }) => {
  const dispatch = useDispatch();
  const { activities, loading } = useSelector(s => s.tripManagement);

  useEffect(() => {
    if (tripId) dispatch(loadTripActivities(tripId));
  }, [tripId, dispatch]);

  if (loading.activities) {
    return <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>;
  }

  if (!activities.length) {
    return <Alert severity="info">No activity recorded yet.</Alert>;
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" mb={2}>
        {activities.length} activities recorded
      </Typography>
      {activities.map((act, i) => (
        <TimelineItem key={act._id} activity={act} isLast={i === activities.length - 1} />
      ))}
    </Box>
  );
};

export default ActivityTimeline;
